import { i18n } from "@lingui/core";
import { Brand, Subvertical } from "../constants.js";
import { eventReference } from "../models/trackingEvent.js";

export enum ToolType {
    CONTRACT = "contract",
    PRICE_VALUATION = "price_valuation",
    SMIDIG_BIL = "smidig_bil",
    SALES = "sales",
}

export enum ToolRoute {
    PRICE_EVALUATION = "/mobility/insights/price-valuation",
    SMIDIG_BILHANDEL = "/motor/artikler/aktuelt/smidig-bilhandel",
    SELL_CAR = "/selg-bil",
    BLOCKET_CAR_DIGITAL_CONTRACT = "https://swiftcourt.com/sv/kontrakt/blocket/bil",
    BLOCKET_BOAT_DIGITAL_CONTRACT = "https://swiftcourt.com/sv/kontrakt/blocket/bat",
    DBA_CAR_WORTH = "https://www.bilbasen.dk/bilvurdering",
}

export interface ToolItem {
    category: string;
    header: string;
    body?: string;
    route: ToolRoute | string;
    trackingNumber: string;
    placement?: number;
    type?: ToolType;
    icon?: string;
    iconBackground?: string;
}

export const SHARED_TOOLS_CONFIG = (): Record<Brand, Record<Subvertical, ToolItem[]>> => ({
    FINN: {
        car: [
            {
                category: "Tools",
                header: i18n.t({
                    id: "market-frontpage.price-advice",
                    message: "Used car price tips",
                }),
                body: i18n.t({
                    id: "market-frontpage.price-advice-description",
                    message: "Get price estimates from similar cars sold on FINN",
                }),
                route: ToolRoute.PRICE_EVALUATION,
                trackingNumber: eventReference.PRICE_TIPS,
                placement: 1,
                type: ToolType.PRICE_VALUATION,
                icon: "Money",
                iconBackground: "s-bg-warning-subtle",
            },
            {
                category: "Tools",
                header: i18n.t({
                    id: "market-frontpage.smidig-bilhandel",
                    message: "Smooth car trade",
                }),
                body: i18n.t({
                    id: "market-frontpage.smidig-bilhandel-description",
                    message: "Safe and easy car trade with Smidig: payment, ownership transfer and contract.",
                }),
                route: ToolRoute.SMIDIG_BILHANDEL,
                trackingNumber: eventReference.CAR_TRADING,
                placement: 2,
                type: ToolType.SMIDIG_BIL,
                icon: "Warranty",
                iconBackground: "s-bg-primary-subtle",
            },
            {
                category: "Tools",
                header: i18n.t({
                    id: "market-frontpage.sell-car",
                    message: "Three ways to sell your car",
                }),
                body: i18n.t({
                    id: "market-frontpage.sell-car-description",
                    message: "Enter your registration number and find the sales option that best suits you",
                }),
                route: ToolRoute.SELL_CAR,
                trackingNumber: eventReference.SELL_YOUR_CAR,
                placement: 3,
                type: ToolType.SALES,
                icon: "Bulb",
                iconBackground: "s-bg-positive-subtle",
            },
        ],
        boat: [],
        mc: [],
        b2b: [],
    },
    BLOCKET: {
        car: [
            {
                category: "Tools",
                header: i18n.t({
                    id: "market-frontpage.digital-contract",
                    message: "Purchase contract",
                }),
                body: i18n.t({
                    id: "market-frontpage.digital-contract-description-car",
                    message: "Make the car deal safe and easy",
                }),
                route: ToolRoute.BLOCKET_CAR_DIGITAL_CONTRACT,
                trackingNumber: eventReference.CONTRACT_OF_SALE,
                placement: 1,
                type: ToolType.CONTRACT,
                icon: "BlocketIcon",
                iconBackground: "s-bg-info-subtle",
            },
        ],
        boat: [
            {
                category: "Tools",
                header: i18n.t({
                    id: "market-frontpage.digital-contract",
                    message: "Purchase contract",
                }),
                body: i18n.t({
                    id: "market-frontpage.digital-contract-description-boat",
                    message: "Make the boat deal safe and easy",
                }),
                route: ToolRoute.BLOCKET_BOAT_DIGITAL_CONTRACT,
                trackingNumber: eventReference.CONTRACT_OF_SALE,
                placement: 1,
                type: ToolType.CONTRACT,
                icon: "BlocketIcon",
                iconBackground: "s-bg-info-subtle",
            },
        ],
        mc: [],
        b2b: [],
    },
    DBA: {
        car: [
            {
                category: "Tools",
                header: i18n.t({
                    id: "market-frontpage.car-worth-title",
                    message: "What is my car worth?",
                }),
                body: i18n.t({
                    id: "market-frontpage.car-worth-description",
                    message:
                        "Find out what your car is worth on our sister site, Bilbasen. Enter your registration number and get a guide price – based on thousands of current transactions.",
                }),
                route: ToolRoute.DBA_CAR_WORTH,
                trackingNumber: eventReference.CAR_WORTH,
                placement: 1,
                type: ToolType.PRICE_VALUATION,
                icon: "Calculator",
                iconBackground: "s-bg-warning-subtle",
            },
        ],
        boat: [],
        mc: [],
        b2b: [],
    },
});

export const getFullUrl = (route: string, brand: Brand): string => {
    if (route.startsWith("http://") || route.startsWith("https://")) {
        return route;
    }

    switch (brand) {
        case Brand.FINN:
            return `https://www.finn.no${route}`;
        case Brand.BLOCKET:
            return `https://www.blocket.se${route}`;
        case Brand.DBA:
            return `https://www.dba.dk${route}`;
        default:
            return route;
    }
};
